import {
    Directive,
    ViewContainerRef,
    TemplateRef,
    AfterViewInit,
    OnDestroy,
    inject,
} from '@angular/core';

/**
 * Structural directive that renders content only when it first enters the viewport.
 *
 * This directive delays creating the embedded view until the observed container
 * becomes visible in the viewport. It improves initial rendering performance by
 * skipping off‑screen DOM work. Content is rendered once on first intersection and
 * remains in the DOM afterward.
 *
 * How it works
 * - Uses Angular dependency injection via `inject()` to access `ViewContainerRef`
 *   and `TemplateRef`:
 *   - `ViewContainerRef` — where the embedded view is created and inserted.
 *   - `TemplateRef` — the template captured from the host.
 * - Internally creates an `IntersectionObserver` to watch the container element.
 * - When an intersection occurs, the template is instantiated once.
 *
 * Example:
 * ```html
 * @for (photo of photos; track photo.id) {
 *   <div class="photo-card" *inView>
 *     @let altText = 'Photo: ' + photo.title;
 *     <img [src]="photo.src" [alt]="altText" loading="lazy" />
 *     <p>{{ photo.title }}</p>
 *   </div>
 * }
 * ```
 *
 * Notes
 * - The observer watches the parent element of the generated comment node for the
 *   structural directive. If the container has zero width/height, a minimal size
 *   (40×40) is applied to make intersection possible.
 */
@Directive({ selector: '[inView]', standalone: true })
export class InView implements AfterViewInit, OnDestroy {
    /** View container where the embedded view will be created on first intersection. */
    protected vcRef = inject(ViewContainerRef);

    /** Template captured from the` host. */
    protected tplRef = inject(TemplateRef);

    /** Flag to ensure the template is rendered only once. */
    alreadyRendered: boolean;

    /**
     * Intersection observer used to detect when the container enters the viewport.
     * Initialized in `ngAfterViewInit` and disconnected in `ngOnDestroy`.
     */
    protected observer: IntersectionObserver;

    /**
     * Sets up the `IntersectionObserver` and begins observing the host container.
     */
    ngAfterViewInit() {
        const commentEl = this.vcRef.element.nativeElement;
        const elToObserve = commentEl.parentElement;
        this.setMinWidthHeight(elToObserve);

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    this.renderContents(entry.isIntersecting);
                });
            },
            { threshold: [0, 0.1, 0.9, 1] },
        );
        this.observer.observe(elToObserve);
    }

    /** Disconnects the observer to avoid memory leaks. */
    ngOnDestroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    /**
     * Creates the embedded view if the container is intersecting and it has not
     * been rendered before.
     * @param isInView Whether the container is currently intersecting the viewport.
     */
    renderContents(isInView) {
        if (isInView && !this.alreadyRendered) {
            this.vcRef.clear();
            this.vcRef.createEmbeddedView(this.tplRef);
            this.alreadyRendered = true;
        }
    }

    /**
     * Applies a minimal width/height to the container if its computed size is 0,
     * ensuring the observer can detect intersections.
     * @param el The container element being observed.
     */
    setMinWidthHeight(el) {
        // prevent issue being visible all together
        const style = window.getComputedStyle(el);
        const [width, height] = [parseInt(style.width), parseInt(style.height)];
        if (!width) {
            el.style.minWidth = '40px';
        }
        if (!height) {
            el.style.minHeight = '40px';
        }
    }
}
